import React, { useEffect, useState } from "react";
import { Button, Checkbox, Combobox, Field, Input } from "@/components/ui";
import { Controller, useForm } from "react-hook-form";
import { SmartLinkExtraParam, SmartLinkParamType, SmartLinkServices } from "@/services/bedrock";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

type ValueTypeOption = { label: string; value: SmartLinkParamType };
export const VALUE_TYPES: ValueTypeOption[] = [
  { label: "Social Network Name", value: "SOCIAL_NETWORK" },
  { label: "Campaign/Prompt Name", value: "SOURCE_NAME" },
  { label: "Player ID", value: "PLAYER_GUID" },
  { label: "Social Post ID", value: "SOCIAL_POST" },
  { label: "Player Email", value: "PLAYER_EMAIL" },
  { label: "Player Internal ID", value: "PLAYER_INTERNAL_ID" },
  { label: "Custom Value", value: "STATIC" },
];

const paramSchema = z
  .object({
    queryKey: z.string().min(1, "Key is required").max(50, "Key is too long"),
    valueType: z.string().min(1, "Value type is required"),
    value: z.string().optional(),
    applyToAllLinks: z.boolean(),
  })
  .refine(data => data.valueType !== "STATIC" || (data.value && data.value.length > 0), {
    message: "Static value is required",
    path: ["value"],
  });

type ParamFormValues = z.infer<typeof paramSchema>;

interface Props {
  smartLinkId?: number;
  setShowForm: (show: boolean) => void;
  valueType: SmartLinkParamType;
  handleReload: () => void;
  editingParam: Partial<SmartLinkExtraParam> | null;
  onUpdate: (param: Partial<SmartLinkExtraParam> | null) => void;
  isCreatingNewLink?: boolean;
  onPendingParamSubmit?: (param: Partial<SmartLinkExtraParam>) => void;
}

export function ExtraParamForm({
  smartLinkId,
  setShowForm,
  editingParam,
  onUpdate,
  handleReload,
  isCreatingNewLink,
  onPendingParamSubmit,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ParamFormValues>({
    resolver: zodResolver(paramSchema),
    defaultValues: {
      queryKey: "",
      valueType: "SOCIAL_NETWORK",
      value: "",
      applyToAllLinks: !smartLinkId && !isCreatingNewLink,
    },
  });
  const valueType = form.watch("valueType");

  const onSubmit = async (data: ParamFormValues) => {
    setIsSubmitting(true);
    try {
      const finalSmartLinkId = data.applyToAllLinks ? null : smartLinkId;

      if (isCreatingNewLink && onPendingParamSubmit && !data.applyToAllLinks) {
        onPendingParamSubmit({
          ...editingParam,
          queryKey: data.queryKey,
          valueType: data.valueType as SmartLinkParamType,
          value: data.value,
        });
        toast.success("Parameter saved (pending link creation)");
        handleReload();
        setShowForm(false);
        onUpdate(null);
        form.reset();
        return;
      }

      if (editingParam && editingParam.extraParamId) {
        await SmartLinkServices.updateSmartLinkExtraParam(editingParam.extraParamId, {
          ...(editingParam as SmartLinkExtraParam),
          ...data,
          valueType: data.valueType as SmartLinkParamType,
          smartLinkId: finalSmartLinkId,
        });
        toast.success("Parameter updated");
      } else {
        await SmartLinkServices.createSmartLinkExtraParam({
          queryKey: data.queryKey,
          valueType: data.valueType as SmartLinkParamType,
          value: data.value,
          smartLinkId: finalSmartLinkId,
        });
        toast.success("Parameter created");
      }
      handleReload();
      setShowForm(false);
      onUpdate(null);
      form.reset();
    } catch (error) {
      toast.error("Failed to save parameter");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (editingParam) {
      form.reset({
        queryKey: editingParam.queryKey || "",
        valueType: editingParam.valueType || "SOCIAL_NETWORK",
        value: editingParam.value || "",
        applyToAllLinks: !editingParam.smartLinkId && !isCreatingNewLink,
      });
    } else {
      form.reset({
        queryKey: "",
        valueType: "SOCIAL_NETWORK",
        value: "",
        applyToAllLinks: !smartLinkId && !isCreatingNewLink,
      });
    }
  }, [editingParam, smartLinkId, isCreatingNewLink]);

  return (
    <form
      onSubmit={e => {
        e.stopPropagation();
        form.handleSubmit(onSubmit)(e);
      }}
      className="p-4 border rounded-xl bg-slate-50 flex flex-col gap-4 shadow-sm mb-2"
    >
      <div className="flex flex-col gap-4">
        <Controller
          name="queryKey"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <Field.Label>Parameter Key</Field.Label>
              <Input {...field} placeholder="e.g. user_id" disabled={isSubmitting} className={"bg-white"} />
              {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
            </Field>
          )}
        />
        <Controller
          name="valueType"
          control={form.control}
          render={({ field }) => (
            <Field>
              <Field.Label>Value Type</Field.Label>
              <Combobox
                items={VALUE_TYPES}
                value={VALUE_TYPES.find(v => v.value === field.value)}
                onValueChange={(v: ValueTypeOption | null) => field.onChange(v?.value)}
                itemToStringLabel={item => item?.label || ""}
                placeholder="Select type..."
                className="w-full bg-white"
              />
            </Field>
          )}
        />
        {valueType === "STATIC" && (
          <Controller
            name="value"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <Field.Label>Static Value</Field.Label>
                <Input {...field} placeholder="e.g. premium_user" disabled={isSubmitting} className={"bg-white"} />
                {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
              </Field>
            )}
          />
        )}

        {(smartLinkId || isCreatingNewLink) && (
          <Controller
            name="applyToAllLinks"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                label="Apply to all links"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isSubmitting}
                className="bg-white"
              />
            )}
          />
        )}
      </div>
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200/60">
        <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" size="sm" loading={isSubmitting}>
          Save
        </Button>
      </div>
    </form>
  );
}
